import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useId, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { outsideOf, useDismissLayer } from "../../shared/lib/dismissLayers";
import { useShortcuts } from "../../shared/lib/shortcuts";
import { PHONE_QUERY, useMediaQuery } from "../../shared/lib/useMediaQuery";
import { attentionCount } from "../../app/flags";
import { queryKeys, useNeedsAttention } from "../../shared/api/queries";
import { useSession } from "../../shared/session/session-context";
import { notificationKeys, removeLists, useNotificationCount } from "./api";
import { NotificationPanel } from "./NotificationPanel";
import { notificationText } from "./notificationText";
import { bellState } from "./rowModel";
import "./notifications.css";

/** The platform close request (Android Back, Esc) where the browser has one; absent elsewhere. */
interface CloseWatcherLike {
  onclose: (() => void) | null;
  destroy(): void;
}
type CloseWatcherCtor = new () => CloseWatcherLike;

/**
 * The header bell (batch 17, notifications panel design §1–§2; flag `Features__NotificationPanel`), merged with the old
 * "Needs attention" pill (attention-bell merge design §1–§4, ruling D1 (a)): the chip BESIDE the glyph is the Needs-attention
 * total (work waiting on you), the dot on the glyph's shoulder is any unread notification. `n` toggles the panel (when
 * single-key shortcuts are on); Esc or a click outside closes it, Esc returning focus to the bell. Below 760 px the panel is a
 * bottom sheet with a Close button; the system Back gesture closes the sheet where the browser offers a close request
 * (CloseWatcher).
 */
export function NotificationBell() {
  const [open, setOpen] = useState(false);
  const panelId = useId();
  const buttonId = useId();
  const rootRef = useRef<HTMLDivElement>(null);
  const panelRef = useRef<HTMLDivElement>(null);
  const phone = useMediaQuery(PHONE_QUERY);
  const client = useQueryClient();
  const session = useSession();
  const count = useNotificationCount(true);
  // §3.3: the header polls Needs attention on the bell's cadence; the panel and Home read the same query.
  const attention = useNeedsAttention(true, { poll: true });
  const state = bellState(
    { data: attention.data ? attentionCount(attention.data, session) : undefined, failed: attention.isError },
    { data: count.data, failed: count.isError },
  );

  const focusBell = () => document.getElementById(buttonId)?.focus();
  const close = (returnFocus: boolean) => {
    setOpen(false);
    if (returnFocus) focusBell();
  };

  // Every opening starts fresh: the lists are dropped on close, and both counts are re-read on open (design §1.2, §2.3;
  // merge §3.3).
  useEffect(() => {
    if (!open) return undefined;
    void client.invalidateQueries({ queryKey: notificationKeys.count });
    void client.invalidateQueries({ queryKey: queryKeys.needsAttention });
    return () => {
      // Only the panel's own lists: a full page open underneath keeps its rows (design §7).
      removeLists(client, "panel");
    };
  }, [open, client]);

  useDismissLayer(open, {
    element: () => panelRef.current,
    isOutside: (event) => outsideOf(event, rootRef.current, panelRef.current),
    dismiss: (reason) => close(reason === "escape"),
  });

  // Phone sheet: Back closes the sheet, not the page.
  useEffect(() => {
    const Watcher = (window as unknown as { CloseWatcher?: CloseWatcherCtor }).CloseWatcher;
    if (!open || !phone || !Watcher) return undefined;
    let watcher: CloseWatcherLike | null = null;
    try {
      watcher = new Watcher();
      watcher.onclose = () => setOpen(false);
    } catch {
      watcher = null;
    }
    return () => watcher?.destroy();
  }, [open, phone]);

  useShortcuts({
    n: () => {
      if (open) close(true);
      else setOpen(true);
    },
  });

  const panel = open ? (
    <div ref={panelRef} className={phone ? "gf-notif-layer sheet" : "gf-notif-layer"}>
      <NotificationPanel id={panelId} phone={phone} count={count.data} onClose={() => close(true)} onNavigate={() => close(false)} />
    </div>
  ) : null;

  return (
    <div className="gf-bell" ref={rootRef} data-guide="notifications">
      <button
        id={buttonId}
        type="button"
        className="gf-bell-btn"
        aria-label={state.name}
        aria-expanded={open}
        aria-controls={panelId}
        aria-keyshortcuts="n"
        title={notificationText("bellTip")}
        onClick={() => (open ? close(false) : setOpen(true))}
      >
        <span className="gf-bell-glyph">
        <svg width="20" height="20" viewBox="0 0 20 20" aria-hidden="true" focusable="false">
          <path
            d="M10 2.6a5 5 0 0 0-5 5v3.1c0 .7-.3 1.4-.8 1.9l-.9.9h13.4l-.9-.9c-.5-.5-.8-1.2-.8-1.9V7.6a5 5 0 0 0-5-5ZM8 15.6a2 2 0 0 0 4 0"
            fill="none"
            stroke="currentColor"
            strokeWidth="1.6"
            strokeLinejoin="round"
            strokeLinecap="round"
          />
        </svg>
        {state.dot && <span className="gf-bell-dot" aria-hidden="true" data-testid="bell-dot" />}
        </span>
        {state.chip && (
          <span className="gf-bell-chip" aria-hidden="true" data-testid="bell-chip">
            {state.chip}
          </span>
        )}
      </button>
      {panel && (phone ? createPortal(panel, document.body) : panel)}
    </div>
  );
}
